import React, { useState, useEffect } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/UnitDealDetailsSlider.css';
import { generateColorSelectionAmendment } from '../utils/pdfGenerator';
import DocumentGallery from './DocumentGallery';

interface UnitDealDetailsSliderProps {
  workspaceId: string;
  unitId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

interface UnitDeal {
  _id: string;
  dealId: string | null;
  project: string;
  projectId: string;
  modelCollection: string;
  modelName: string;
  type: string;
  unitNumber: string;
  unitStatus: string;
  deal: string;
  dealStatus: string;
  designStudioStatus: string;
  colorCollection: string;
  packages: string;
  options: string;
  accessToPP: boolean;
  designStudio: boolean;
  colorSelection: boolean;
  packageSelection: boolean;
  parkingSelection: boolean;
  bicycleSelection: boolean;
  lockerSelection: boolean;
  layoutCustomization: boolean;
}

interface PhaseSelection {
  _id: string;
  selectionType: 'color' | 'package' | 'option';
  itemId: string;
  itemName: string;
  cost: number;
  price: number;
  isUpgrade: boolean;
  phase: string;
  location?: string;
  categoryName?: string;
  description?: string;
  constructionNotes?: string;
}

interface ColorCollection {
  _id: string;
  colorCollectionId: string;
  colorCollectionName: string;
  description?: string;
  constructionNotes?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
}

interface Package {
  _id: string;
  packageId: string;
  packageName: string;
  description?: string;
  constructionNotes?: string;
  location?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
  incompatiblePackages?: Array<{ _id: string; name?: string }>;
  incompatiblePersonalizationOptions?: Array<{ _id: string; name?: string }>;
  parentPackage?: { id: string; name?: string } | null;
  includedOptions?: Array<{ _id: string; name?: string }>;
}

interface Option {
  _id: string;
  optionId: string;
  optionName: string;
  description?: string;
  constructionNotes?: string;
  location?: string;
  categoryId?: string;
  categoryName?: string;
  isUpgrade: boolean;
  cost: number;
  price: number;
  incompatibleOptions?: Array<{ _id: string; name?: string }>;
  incompatiblePackages?: Array<{ _id: string; name?: string }>;
  onlySoldWithPackages?: Array<{ _id: string; name?: string }>;
  onlySoldWithOptions?: Array<{ _id: string; name?: string }>;
}

const UnitDealDetailsSlider: React.FC<UnitDealDetailsSliderProps> = ({
  workspaceId,
  unitId,
  onClose,
  onSuccess
}) => {
  // Helper function to strip HTML tags
  const stripHtmlTags = (html: string | undefined): string => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const [unitDeal, setUnitDeal] = useState<UnitDeal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('available-selections');
  const [projectOngoingPhase, setProjectOngoingPhase] = useState<string>('One');

  // Available Selections state
  const [colorCollections, setColorCollections] = useState<ColorCollection[]>([]);
  const [selectedColorCollectionId, setSelectedColorCollectionId] = useState<string | null>(null);
  const [loadingColorCollections, setLoadingColorCollections] = useState(false);

  // Packages state
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageIds, setSelectedPackageIds] = useState<string[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);

  // Options state
  const [options, setOptions] = useState<Option[]>([]);
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Loading states for individual actions
  const [addingColorCollection, setAddingColorCollection] = useState<string | null>(null);
  const [addingPackage, setAddingPackage] = useState<string | null>(null);
  const [removingPackage, setRemovingPackage] = useState<string | null>(null);
  const [addingOption, setAddingOption] = useState<string | null>(null);
  const [removingOption, setRemovingOption] = useState<string | null>(null);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingColorCollectionId, setPendingColorCollectionId] = useState<string | null>(null);

  // Info modal state
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModalData, setInfoModalData] = useState<{
    title: string;
    description?: string;
    constructionNotes?: string;
  } | null>(null);

  // Design Studio state
  const [activePhaseTab, setActivePhaseTab] = useState<string>('One');
  const [phaseSelections, setPhaseSelections] = useState<PhaseSelection[]>([]);
  const [loadingPhaseSelections, setLoadingPhaseSelections] = useState(false);
  const [deletingSelection, setDeletingSelection] = useState<string | null>(null);
  const [phaseStatuses, setPhaseStatuses] = useState<{[key: string]: string}>({
    One: 'NOT STARTED',
    Two: 'NOT STARTED',
    Three: 'NOT STARTED',
    Four: 'NOT STARTED',
    Five: 'NOT STARTED'
  });
  const [showStatusConfirmDialog, setShowStatusConfirmDialog] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{phase: string, status: string} | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  useEffect(() => {
    // Disable body scroll when slider is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when slider closes
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    loadUnitDealDetails();
  }, [workspaceId, unitId]);

  useEffect(() => {
    if (activeTab === 'available-selections' && !loadingColorCollections && colorCollections.length === 0) {
      loadColorCollections();
    }
    if (activeTab === 'design-studio' && !loadingPhaseSelections) {
      loadPhaseSelections(activePhaseTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, activePhaseTab]);

  useEffect(() => {
    if (selectedColorCollectionId) {
      loadPackages();
      loadOptions();
    } else {
      setPackages([]);
      setSelectedPackageIds([]);
      setOptions([]);
      setSelectedOptionIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedColorCollectionId]);

  const loadUnitDealDetails = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}`);
      setUnitDeal(response.data);

      // Load phase statuses from response
      if (response.data.phaseStatuses) {
        setPhaseStatuses(response.data.phaseStatuses);
      }

      // Load project data to get ongoingSelectionPhase
      if (response.data.projectId) {
        loadProjectPhase(response.data.projectId);
      }
    } catch (err: any) {
      console.error('Error loading unit deal details:', err);
      setError(err.response?.data?.message || 'Failed to load unit deal details');
      toast.error('Failed to load unit deal details');
    } finally {
      setLoading(false);
    }
  };

  const loadProjectPhase = async (projectId: string) => {
    try {
      const response = await api.get(`/projects/${workspaceId}/${projectId}`);
      const phase = response.data.ongoingSelectionPhase || 'One';
      setProjectOngoingPhase(phase);
    } catch (err: any) {
      console.error('Error loading project phase:', err);
      // Default to 'One' if we can't load project data
      setProjectOngoingPhase('One');
    }
  };

  const loadPhaseSelections = async (phase: string) => {
    try {
      setLoadingPhaseSelections(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/phase-selections`, {
        params: { phase }
      });
      setPhaseSelections(response.data.selections || []);
    } catch (err: any) {
      console.error('Error loading phase selections:', err);
      toast.error('Failed to load phase selections');
      setPhaseSelections([]);
    } finally {
      setLoadingPhaseSelections(false);
    }
  };

  const loadColorCollections = async () => {
    try {
      setLoadingColorCollections(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/color-collections`);
      setColorCollections(response.data.colorCollections || []);
      setSelectedColorCollectionId(response.data.selectedColorCollectionId || null);
      // Update projectOngoingPhase from the response if available
      if (response.data.ongoingSelectionPhase) {
        setProjectOngoingPhase(response.data.ongoingSelectionPhase);
      }
    } catch (err: any) {
      console.error('Error loading color collections:', err);
      toast.error('Failed to load color collections');
    } finally {
      setLoadingColorCollections(false);
    }
  };

  const handleAddColorCollectionClick = (colorCollectionId: string) => {
    // Check if a different color collection is already selected
    if (selectedColorCollectionId && selectedColorCollectionId !== colorCollectionId) {
      // Show confirmation dialog
      setPendingColorCollectionId(colorCollectionId);
      setShowConfirmDialog(true);
    } else {
      // Proceed directly if no selection exists
      handleAddColorCollection(colorCollectionId);
    }
  };

  const handleConfirmChange = () => {
    if (pendingColorCollectionId) {
      handleAddColorCollection(pendingColorCollectionId);
    }
    setShowConfirmDialog(false);
    setPendingColorCollectionId(null);
  };

  const handleCancelChange = () => {
    setShowConfirmDialog(false);
    setPendingColorCollectionId(null);
  };

  const handleAddColorCollection = async (colorCollectionId: string) => {
    try {
      setAddingColorCollection(colorCollectionId);
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/color-collections`, {
        colorCollectionId
      });

      toast.success(response.data.message || 'Color collection added successfully');

      // Reload color collections to update UI
      await loadColorCollections();

      // Reload packages and options as they depend on color collection
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding color collection:', err);
      toast.error(err.response?.data?.message || 'Failed to add color collection');
    } finally {
      setAddingColorCollection(null);
    }
  };

  const loadPackages = async () => {
    if (!selectedColorCollectionId) {
      setPackages([]);
      setSelectedPackageIds([]);
      return;
    }

    try {
      setLoadingPackages(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/packages`);
      setPackages(response.data.packages || []);
      setSelectedPackageIds(response.data.selectedPackageIds || []);
    } catch (err: any) {
      console.error('Error loading packages:', err);
      toast.error('Failed to load packages');
    } finally {
      setLoadingPackages(false);
    }
  };

  const handleAddPackage = async (packageId: string) => {
    try {
      setAddingPackage(packageId);
      // Add the package (backend will automatically add included options)
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/packages`, {
        packageId
      });

      toast.success(response.data.message || 'Package added successfully');

      // Show info about automatically added included options
      if (response.data.addedIncludedOptions && response.data.addedIncludedOptions.length > 0) {
        const optionNames = response.data.addedIncludedOptions.map((opt: any) => opt.name).join(', ');
        toast.info(`Included options added: ${optionNames}`);
      }

      // Reload packages and options to update UI
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding package:', err);
      toast.error(err.response?.data?.message || 'Failed to add package');
    } finally {
      setAddingPackage(null);
    }
  };

  const handleRemovePackage = async (packageId: string) => {
    try {
      setRemovingPackage(packageId);
      const response = await api.delete(`/unit-deals/${workspaceId}/unit/${unitId}/packages`, {
        data: { packageId }
      });

      toast.success(response.data.message || 'Package removed successfully');

      // Show info about automatically removed included options
      if (response.data.removedIncludedOptions && response.data.removedIncludedOptions.length > 0) {
        const optionNames = response.data.removedIncludedOptions.map((opt: any) => opt.name).join(', ');
        toast.info(`Included options removed: ${optionNames}`);
      }

      // Reload packages and options to update UI
      await loadPackages();
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error removing package:', err);
      toast.error(err.response?.data?.message || 'Failed to remove package');
    } finally {
      setRemovingPackage(null);
    }
  };

  const loadOptions = async () => {
    if (!selectedColorCollectionId) {
      setOptions([]);
      setSelectedOptionIds([]);
      return;
    }

    try {
      setLoadingOptions(true);
      const response = await api.get(`/unit-deals/${workspaceId}/unit/${unitId}/options`);
      setOptions(response.data.options || []);
      setSelectedOptionIds(response.data.selectedOptionIds || []);
    } catch (err: any) {
      console.error('Error loading options:', err);
      toast.error('Failed to load options');
    } finally {
      setLoadingOptions(false);
    }
  };

  const handleAddOption = async (optionId: string) => {
    try {
      setAddingOption(optionId);
      const response = await api.post(`/unit-deals/${workspaceId}/unit/${unitId}/options`, {
        optionId
      });

      toast.success(response.data.message || 'Option added successfully');

      // Reload options to update UI
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error adding option:', err);
      toast.error(err.response?.data?.message || 'Failed to add option');
    } finally {
      setAddingOption(null);
    }
  };

  const handleRemoveOption = async (optionId: string) => {
    try {
      setRemovingOption(optionId);
      const response = await api.delete(`/unit-deals/${workspaceId}/unit/${unitId}/options`, {
        data: { optionId }
      });

      toast.success(response.data.message || 'Option removed successfully');

      // Reload options to update UI
      await loadOptions();

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error removing option:', err);
      toast.error(err.response?.data?.message || 'Failed to remove option');
    } finally {
      setRemovingOption(null);
    }
  };

  // Handle phase status change
  const handleStatusClick = (phase: string, status: string) => {
    // Don't allow changing if it's already the current status
    if (phaseStatuses[phase] === status) {
      return;
    }

    setPendingStatusChange({ phase, status });
    setShowStatusConfirmDialog(true);
  };

  const handleConfirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    try {
      setUpdatingStatus(true);
      const { phase, status } = pendingStatusChange;

      await api.patch(`/unit-deals/${workspaceId}/unit/${unitId}/phase-status`, {
        phase,
        status
      });

      // Update local state
      setPhaseStatuses(prev => ({
        ...prev,
        [phase]: status
      }));

      toast.success('Phase status updated successfully');

      // Close dialog
      setShowStatusConfirmDialog(false);
      setPendingStatusChange(null);
    } catch (err: any) {
      console.error('Error updating phase status:', err);
      toast.error(err.response?.data?.message || 'Failed to update phase status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCancelStatusChange = () => {
    setShowStatusConfirmDialog(false);
    setPendingStatusChange(null);
  };

  // Helper function to check if a package is incompatible with selected packages/options
  const isPackageIncompatible = (pkg: Package): boolean => {
    // Check if incompatible with any selected packages
    if (pkg.incompatiblePackages && pkg.incompatiblePackages.length > 0) {
      const incompatiblePackageIds = pkg.incompatiblePackages.map(p => p._id);
      const hasIncompatiblePackage = selectedPackageIds.some(selectedId =>
        incompatiblePackageIds.includes(selectedId)
      );
      if (hasIncompatiblePackage) return true;
    }

    // Check if incompatible with any selected options
    if (pkg.incompatiblePersonalizationOptions && pkg.incompatiblePersonalizationOptions.length > 0) {
      const incompatibleOptionIds = pkg.incompatiblePersonalizationOptions.map(o => o._id);
      const hasIncompatibleOption = selectedOptionIds.some(selectedId =>
        incompatibleOptionIds.includes(selectedId)
      );
      if (hasIncompatibleOption) return true;
    }

    return false;
  };

  // Helper function to check if an option is incompatible with selected packages/options
  const isOptionIncompatible = (option: Option): boolean => {
    // Check if incompatible with any selected packages
    if (option.incompatiblePackages && option.incompatiblePackages.length > 0) {
      const incompatiblePackageIds = option.incompatiblePackages.map(p => p._id);
      const hasIncompatiblePackage = selectedPackageIds.some(selectedId =>
        incompatiblePackageIds.includes(selectedId)
      );
      if (hasIncompatiblePackage) return true;
    }

    // Check if incompatible with any selected options
    if (option.incompatibleOptions && option.incompatibleOptions.length > 0) {
      const incompatibleOptionIds = option.incompatibleOptions.map(o => o._id);
      const hasIncompatibleOption = selectedOptionIds.some(selectedId =>
        incompatibleOptionIds.includes(selectedId)
      );
      if (hasIncompatibleOption) return true;
    }

    return false;
  };

  // Helper function to check if a package has unsatisfied dependencies
  const getPackageDependencyWarning = (pkg: Package): string | null => {
    // Check if parent package is required but not selected
    if (pkg.parentPackage && pkg.parentPackage.id) {
      const isParentSelected = selectedPackageIds.includes(pkg.parentPackage.id);
      if (!isParentSelected) {
        return `Requires parent package: ${pkg.parentPackage.name || pkg.parentPackage.id}`;
      }
    }

    // Note: Included options are automatically added when the package is selected,
    // so we don't need to check for them here

    return null;
  };

  // Helper function to check if an option has unsatisfied dependencies
  const getOptionDependencyWarning = (option: Option): string | null => {
    // Check if required packages are selected
    if (option.onlySoldWithPackages && option.onlySoldWithPackages.length > 0) {
      const missingPackages = option.onlySoldWithPackages.filter(
        pkg => !selectedPackageIds.includes(pkg._id)
      );
      if (missingPackages.length > 0) {
        const packageNames = missingPackages.map(p => p.name || p._id).join(', ');
        return `Requires packages: ${packageNames}`;
      }
    }

    // Check if required options are selected
    if (option.onlySoldWithOptions && option.onlySoldWithOptions.length > 0) {
      const missingOptions = option.onlySoldWithOptions.filter(
        opt => !selectedOptionIds.includes(opt._id)
      );
      if (missingOptions.length > 0) {
        const optionNames = missingOptions.map(o => o.name || o._id).join(', ');
        return `Requires options: ${optionNames}`;
      }
    }

    return null;
  };

  // Helper function to check if a phase tab should be enabled
  const isPhaseEnabled = (phase: string): boolean => {
    const phaseOrder = ['One', 'Two', 'Three', 'Four', 'Five'];
    const currentPhaseIndex = phaseOrder.indexOf(projectOngoingPhase);
    const checkPhaseIndex = phaseOrder.indexOf(phase);
    return checkPhaseIndex <= currentPhaseIndex;
  };

  // Helper function to check if selections can be modified based on phase status
  // When phase is provided (from Design Studio tab), check that specific phase's status
  // When phase is not provided (from Available Selections tab), use projectOngoingPhase
  const canModifySelections = (selectionType: 'color' | 'package' | 'option', phase?: string): boolean => {
    // Color collections can only be modified in Phase One
    if (selectionType === 'color') {
      const phaseOneStatus = phaseStatuses['One'] || 'NOT STARTED';
      return phaseOneStatus === 'NOT STARTED' || phaseOneStatus === 'OPEN';
    }

    // Packages and options can be modified based on the phase status
    // If phase is provided (Design Studio tab), check that phase's status
    // Otherwise use the project's ongoing phase (Available Selections tab)
    const checkPhase = phase || projectOngoingPhase;
    const currentPhaseStatus = phaseStatuses[checkPhase] || 'NOT STARTED';
    return currentPhaseStatus === 'NOT STARTED' || currentPhaseStatus === 'OPEN';
  };

  // Helper function to delete a phase selection
  const handleDeletePhaseSelection = async (selectionId: string, selectionType: string) => {
    try {
      setDeletingSelection(selectionId);

      let endpoint = '';
      let itemIdField = '';

      // Find the selection to get its itemId
      const selection = phaseSelections.find(s => s._id === selectionId);
      if (!selection) {
        toast.error('Selection not found');
        return;
      }

      // Determine endpoint based on selection type
      if (selectionType === 'color') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/color-collections`;
        itemIdField = 'colorCollectionId';
      } else if (selectionType === 'package') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/packages`;
        itemIdField = 'packageId';
      } else if (selectionType === 'option') {
        endpoint = `/unit-deals/${workspaceId}/unit/${unitId}/options`;
        itemIdField = 'optionId';
      }

      const response = await api.delete(endpoint, {
        data: { [itemIdField]: selection.itemId }
      });

      toast.success(response.data.message || 'Selection deleted successfully');

      // Reload phase selections
      await loadPhaseSelections(activePhaseTab);

      // Optionally call onSuccess callback
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      console.error('Error deleting selection:', err);
      toast.error(err.response?.data?.message || 'Failed to delete selection');
    } finally {
      setDeletingSelection(null);
    }
  };

  // Handle Color Selection Amendment PDF generation
  const handleGenerateColorSelectionAmendment = async () => {
    try {
      setGeneratingPdf(true);

      // Get color collection from phase selections
      const colorSelection = phaseSelections.find(s => s.selectionType === 'color');

      if (!colorSelection) {
        toast.error('No color collection selected for this phase');
        setGeneratingPdf(false);
        return;
      }

      if (!unitDeal) {
        toast.error('Unit deal information not available');
        setGeneratingPdf(false);
        return;
      }

      // Fetch project logo, color collection hero image, and purchaser names from API
      let projectLogo: string | undefined = undefined;
      let colorSwatchImage: string | undefined = undefined;
      let purchasers: Array<{ name: string }> = [{ name: 'Purchaser' }];

      try {
        const imagesResponse = await api.get(
          `/unit-deals/${workspaceId}/unit/${unitId}/pdf-images?phase=${activePhaseTab}`
        );

        if (imagesResponse.data) {
          projectLogo = imagesResponse.data.projectLogo || undefined;
          colorSwatchImage = imagesResponse.data.colorCollectionHero || undefined;
          // Use purchasers from API if available
          if (imagesResponse.data.purchasers && imagesResponse.data.purchasers.length > 0) {
            purchasers = imagesResponse.data.purchasers;
          }
        }
      } catch (imgErr) {
        console.warn('Could not fetch images for PDF, continuing without them:', imgErr);
        // Fallback to splitting the deal string if API fails
        const purchaserNamesList = unitDeal.deal ? unitDeal.deal.split(',').map(name => name.trim()) : [];
        if (purchaserNamesList.length > 0) {
          purchasers = purchaserNamesList.map(name => ({ name }));
        }
      }

      // Generate the PDF
      generateColorSelectionAmendment({
        projectName: unitDeal.project || 'Project',
        unitNumber: unitDeal.unitNumber || '',
        level: '', // Can be added if available in future
        suiteNo: unitDeal.unitNumber || '',
        purchasers: purchasers,
        colorCollectionName: colorSelection.itemName,
        vendorName: 'Vendor', // Can be fetched from project/builder if needed
        projectLogo: projectLogo,
        colorSwatchImage: colorSwatchImage
      });

      toast.success('Color Selection Amendment PDF generated successfully');
    } catch (err: any) {
      console.error('Error generating Color Selection Amendment:', err);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div className="unit-deal-details-slider">
      {/* Header */}
      <div className="unit-deal-details-header">
        <div className="unit-deal-details-header-left">
          <button
            onClick={onClose}
            className="unit-deal-details-back-btn"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="unit-deal-details-title">
            <h2>
              {loading ? 'Loading...' : unitDeal ? `${unitDeal.project} - Unit ${unitDeal.unitNumber}` : 'Unit Details'}
            </h2>
            <p>
              {loading ? '' : unitDeal ? `Unit Status: ${unitDeal.unitStatus} | Deal Status: ${unitDeal.dealStatus || 'N/A'}` : 'Unit and Deal Management Details'}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="unit-deal-details-close-btn"
          aria-label="Close"
        >
          <X size={24} />
        </button>
      </div>

      {/* Tabs */}
      {!loading && !error && (
        <div className="unit-deal-details-tabs">
          <button
            className={`unit-deal-details-tab ${activeTab === 'available-selections' ? 'active' : ''}`}
            onClick={() => setActiveTab('available-selections')}
          >
            Available Selections
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'design-studio' ? 'active' : ''}`}
            onClick={() => setActiveTab('design-studio')}
          >
            Design Studio
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'parking-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('parking-selection')}
          >
            Parking Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'bicycle-parking-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('bicycle-parking-selection')}
          >
            Bicycle Parking Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'locker-selection' ? 'active' : ''}`}
            onClick={() => setActiveTab('locker-selection')}
          >
            Locker Selection
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'layout-customizations' ? 'active' : ''}`}
            onClick={() => setActiveTab('layout-customizations')}
          >
            Layout Customizations
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </button>
          <button
            className={`unit-deal-details-tab ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>
      )}

      {/* Content */}
      <div className="unit-deal-details-content">
        {loading ? (
          <div className="unit-deal-details-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : error ? (
          <div className="unit-deal-details-error">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {activeTab === 'available-selections' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {/* Color Collections Section */}
                <div className="unit-deal-section">
                  <div className="unit-deal-section-header">
                    <h3>Color Collection</h3>
                  </div>

                  {loadingColorCollections ? (
                    <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <div className="builders-loading-spinner" />
                    </div>
                  ) : colorCollections.length === 0 ? (
                    <div className="unit-deal-empty-state">
                      <p>No color collections available for this unit</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th style={{ textAlign: 'right' }}>Cost</th>
                            <th style={{ textAlign: 'right' }}>Price</th>
                            <th style={{ textAlign: 'center' }}>Info</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {colorCollections.map((collection) => (
                            <tr key={collection._id}>
                              <td className="data-table-primary-cell">{collection.colorCollectionName}</td>
                              <td style={{ textAlign: 'right' }}>${collection.cost.toFixed(2)}</td>
                              <td style={{ textAlign: 'right' }}>${collection.price.toFixed(2)}</td>
                              <td style={{ textAlign: 'center' }}>
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                  <button
                                    className="btn btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setInfoModalData({
                                        title: collection.colorCollectionName,
                                        description: collection.description,
                                        constructionNotes: collection.constructionNotes
                                      });
                                      setShowInfoModal(true);
                                    }}
                                    title="View Details"
                                    style={{
                                      width: '32px',
                                      height: '32px',
                                      borderRadius: '50%',
                                      padding: '0',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: '#48182F',
                                      color: 'white',
                                      border: 'none',
                                      fontSize: '14px',
                                      fontWeight: '600',
                                      fontStyle: 'italic',
                                      cursor: 'pointer',
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                  >
                                    i
                                  </button>
                                </div>
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {selectedColorCollectionId === collection.colorCollectionId ? (
                                  <button
                                    className="btn btn-sm btn-success"
                                    disabled
                                    style={{
                                      backgroundColor: '#10B981',
                                      color: 'white',
                                      cursor: 'default',
                                      opacity: 1
                                    }}
                                  >
                                    Selected
                                  </button>
                                ) : (
                                  <>
                                    {!canModifySelections('color') ? (
                                      <button
                                        className="btn btn-sm btn-secondary"
                                        disabled
                                        style={{
                                          opacity: 0.6,
                                          cursor: 'not-allowed'
                                        }}
                                        title={projectOngoingPhase !== 'One'
                                          ? "Color collection can only be modified in Phase One"
                                          : `Color collection can only be modified when Phase One status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses['One']}`
                                        }
                                      >
                                        Locked
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleAddColorCollectionClick(collection.colorCollectionId);
                                        }}
                                        disabled={addingColorCollection === collection.colorCollectionId}
                                        style={{
                                          opacity: addingColorCollection === collection.colorCollectionId ? 0.7 : 1
                                        }}
                                      >
                                        {addingColorCollection === collection.colorCollectionId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                            <td className="data-table-primary-cell">Total</td>
                            <td style={{ textAlign: 'right' }}>
                              ${colorCollections
                                .filter(col => selectedColorCollectionId === col.colorCollectionId)
                                .reduce((sum, col) => sum + col.cost, 0)
                                .toFixed(2)}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              ${colorCollections
                                .filter(col => selectedColorCollectionId === col.colorCollectionId)
                                .reduce((sum, col) => sum + col.price, 0)
                                .toFixed(2)}
                            </td>
                            <td></td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Packages Section */}
                {selectedColorCollectionId && (
                  <div className="unit-deal-section">
                    <div className="unit-deal-section-header">
                      <h3>Packages</h3>
                    </div>

                    {loadingPackages ? (
                      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="builders-loading-spinner" />
                      </div>
                    ) : packages.length === 0 ? (
                      <div className="unit-deal-empty-state">
                        <p>No packages available for this color collection</p>
                      </div>
                    ) : (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Location</th>
                              <th style={{ textAlign: 'right' }}>Cost</th>
                              <th style={{ textAlign: 'right' }}>Price</th>
                              <th style={{ textAlign: 'center' }}>Info</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {packages.map((pkg) => {
                              const isIncompatible = isPackageIncompatible(pkg);
                              const isSelected = selectedPackageIds.includes(pkg.packageId);
                              const dependencyWarning = getPackageDependencyWarning(pkg);
                              const hasUnsatisfiedDependency = dependencyWarning !== null;

                              // Hide incompatible packages that are not selected
                              if (isIncompatible && !isSelected) {
                                return null;
                              }

                              return (
                                <tr key={pkg._id} style={{ opacity: isIncompatible ? 0.5 : 1 }}>
                                  <td className="data-table-primary-cell">
                                    {pkg.packageName}
                                    {isIncompatible && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '11px',
                                        color: '#EF4444',
                                        fontWeight: '600'
                                      }}>
                                        (Incompatible)
                                      </span>
                                    )}
                                    {hasUnsatisfiedDependency && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#F59E0B',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        {dependencyWarning}
                                      </div>
                                    )}
                                    {pkg.includedOptions && pkg.includedOptions.length > 0 && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#10B981',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        Includes: {pkg.includedOptions.map(o => o.name || o._id).join(', ')}
                                      </div>
                                    )}
                                  </td>
                                  <td>{pkg.location || '-'}</td>
                                  <td style={{ textAlign: 'right' }}>${pkg.cost.toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${pkg.price.toFixed(2)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInfoModalData({
                                            title: pkg.packageName,
                                            description: pkg.description,
                                            constructionNotes: pkg.constructionNotes
                                          });
                                          setShowInfoModal(true);
                                        }}
                                        title="View Details"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          padding: '0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#48182F',
                                          color: 'white',
                                          border: 'none',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          fontStyle: 'italic',
                                          cursor: 'pointer',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                      >
                                        i
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {isSelected ? (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package')) {
                                            toast.error(`Packages can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else {
                                            handleRemovePackage(pkg.packageId);
                                          }
                                        }}
                                        disabled={!canModifySelections('package') || removingPackage === pkg.packageId}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('package') || removingPackage === pkg.packageId) ? 0.7 : 1,
                                          cursor: !canModifySelections('package') ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {removingPackage === pkg.packageId ? 'Removing...' : 'Remove'}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package')) {
                                            toast.error(`Packages can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else if (hasUnsatisfiedDependency) {
                                            toast.warning(dependencyWarning);
                                          } else {
                                            handleAddPackage(pkg.packageId);
                                          }
                                        }}
                                        disabled={!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId}
                                        style={{
                                          opacity: (!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId) ? 0.7 : 1,
                                          cursor: (!canModifySelections('package') || isIncompatible || hasUnsatisfiedDependency || addingPackage === pkg.packageId) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package') ? `Locked: ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {addingPackage === pkg.packageId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                              <td className="data-table-primary-cell">Total</td>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>
                                ${packages
                                  .filter(pkg => selectedPackageIds.includes(pkg.packageId))
                                  .reduce((sum, pkg) => sum + pkg.cost, 0)
                                  .toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                ${packages
                                  .filter(pkg => selectedPackageIds.includes(pkg.packageId))
                                  .reduce((sum, pkg) => sum + pkg.price, 0)
                                  .toFixed(2)}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Options Section */}
                {selectedColorCollectionId && (
                  <div className="unit-deal-section">
                    <div className="unit-deal-section-header">
                      <h3>Options</h3>
                    </div>

                    {loadingOptions ? (
                      <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="builders-loading-spinner" />
                      </div>
                    ) : options.length === 0 ? (
                      <div className="unit-deal-empty-state">
                        <p>No options available for this color collection</p>
                      </div>
                    ) : (
                      <div className="data-table-container">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Location</th>
                              <th>Category</th>
                              <th style={{ textAlign: 'right' }}>Cost</th>
                              <th style={{ textAlign: 'right' }}>Price</th>
                              <th style={{ textAlign: 'center' }}>Info</th>
                              <th style={{ textAlign: 'center' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {options.map((option) => {
                              const isIncompatible = isOptionIncompatible(option);
                              const isSelected = selectedOptionIds.includes(option.optionId);
                              const dependencyWarning = getOptionDependencyWarning(option);
                              const hasUnsatisfiedDependency = dependencyWarning !== null;

                              // Hide incompatible options that are not selected
                              if (isIncompatible && !isSelected) {
                                return null;
                              }

                              return (
                                <tr key={option._id} style={{ opacity: isIncompatible ? 0.5 : 1 }}>
                                  <td className="data-table-primary-cell">
                                    {option.optionName}
                                    {isIncompatible && (
                                      <span style={{
                                        marginLeft: '8px',
                                        fontSize: '11px',
                                        color: '#EF4444',
                                        fontWeight: '600'
                                      }}>
                                        (Incompatible)
                                      </span>
                                    )}
                                    {hasUnsatisfiedDependency && !isSelected && (
                                      <div style={{
                                        fontSize: '11px',
                                        color: '#F59E0B',
                                        marginTop: '4px',
                                        fontStyle: 'italic'
                                      }}>
                                        {dependencyWarning}
                                      </div>
                                    )}
                                  </td>
                                  <td>{option.location || '-'}</td>
                                  <td>{option.categoryName || '-'}</td>
                                  <td style={{ textAlign: 'right' }}>${option.cost.toFixed(2)}</td>
                                  <td style={{ textAlign: 'right' }}>${option.price.toFixed(2)}</td>
                                  <td style={{ textAlign: 'center' }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                      <button
                                        className="btn btn-sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setInfoModalData({
                                            title: option.optionName,
                                            description: option.description,
                                            constructionNotes: option.constructionNotes
                                          });
                                          setShowInfoModal(true);
                                        }}
                                        title="View Details"
                                        style={{
                                          width: '32px',
                                          height: '32px',
                                          borderRadius: '50%',
                                          padding: '0',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          backgroundColor: '#48182F',
                                          color: 'white',
                                          border: 'none',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          fontStyle: 'italic',
                                          cursor: 'pointer',
                                          transition: 'opacity 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                      >
                                        i
                                      </button>
                                    </div>
                                  </td>
                                  <td style={{ textAlign: 'center' }}>
                                    {isSelected ? (
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option')) {
                                            toast.error(`Options can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else {
                                            handleRemoveOption(option.optionId);
                                          }
                                        }}
                                        disabled={!canModifySelections('option') || removingOption === option.optionId}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('option') || removingOption === option.optionId) ? 0.7 : 1,
                                          cursor: !canModifySelections('option') ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {removingOption === option.optionId ? 'Removing...' : 'Remove'}
                                      </button>
                                    ) : (
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option')) {
                                            toast.error(`Options can only be modified when ${projectOngoingPhase} status is "NOT STARTED" or "OPEN"`);
                                          } else if (hasUnsatisfiedDependency) {
                                            toast.warning(dependencyWarning);
                                          } else {
                                            handleAddOption(option.optionId);
                                          }
                                        }}
                                        disabled={!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId}
                                        style={{
                                          opacity: (!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId) ? 0.7 : 1,
                                          cursor: (!canModifySelections('option') || isIncompatible || hasUnsatisfiedDependency || addingOption === option.optionId) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option') ? `Locked: Phase ${projectOngoingPhase} status is ${phaseStatuses[projectOngoingPhase]}` : ''}
                                      >
                                        {addingOption === option.optionId ? 'Adding...' : 'Add'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                            <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                              <td className="data-table-primary-cell">Total</td>
                              <td></td>
                              <td></td>
                              <td style={{ textAlign: 'right' }}>
                                ${options
                                  .filter(opt => selectedOptionIds.includes(opt.optionId))
                                  .reduce((sum, opt) => sum + opt.cost, 0)
                                  .toFixed(2)}
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                ${options
                                  .filter(opt => selectedOptionIds.includes(opt.optionId))
                                  .reduce((sum, opt) => sum + opt.price, 0)
                                  .toFixed(2)}
                              </td>
                              <td></td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'design-studio' && (
              <div style={{ gridColumn: '1 / -1', padding: 0 }}>
                {/* Phase Tabs */}
                <div className="unit-deal-details-tabs" style={{ borderBottom: '1px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                  {['One', 'Two', 'Three', 'Four', 'Five'].map((phase) => (
                    <button
                      key={phase}
                      className={`unit-deal-details-tab ${activePhaseTab === phase ? 'active' : ''}`}
                      onClick={() => {
                        if (isPhaseEnabled(phase)) {
                          setActivePhaseTab(phase);
                        }
                      }}
                      disabled={!isPhaseEnabled(phase)}
                      style={{
                        opacity: isPhaseEnabled(phase) ? 1 : 0.5,
                        cursor: isPhaseEnabled(phase) ? 'pointer' : 'not-allowed'
                      }}
                    >
                      Phase {phase}
                    </button>
                  ))}
                </div>

                {/* Phase Content */}
                {loadingPhaseSelections ? (
                  <div style={{ padding: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="builders-loading-spinner" />
                  </div>
                ) : (
                  <>
                    {/* Phase Stage Progress Card */}
                    <div style={{
                      background: 'white',
                      padding: '24px 40px',
                      borderBottom: '1px solid #E5E7EB',
                      overflowX: 'auto',
                      overflowY: 'hidden'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0,
                        position: 'relative',
                        minWidth: 'max-content'
                      }}>
                        {['NOT STARTED', 'OPEN', 'SUBMITTED', 'SELECTION FINAL', 'WAITING FOR PAYMENT', 'COMPLETED', 'CANCELLED - BY CUSTOMER', 'CANCELLED - NO PAYMENT'].map((stage, index) => {
                          // Get the current phase status
                          const currentStatus = phaseStatuses[activePhaseTab] || 'NOT STARTED';
                          const isActive = stage === currentStatus;
                          const isFirst = index === 0;
                          const isLast = index === 7;

                          return (
                            <div
                              key={stage}
                              onClick={() => handleStatusClick(activePhaseTab, stage)}
                              style={{
                                flexShrink: 0,
                                minWidth: '220px',
                                padding: '10px 24px',
                                paddingLeft: isFirst ? '28px' : '24px',
                                paddingRight: isLast ? '28px' : '24px',
                                background: isActive ? '#D4CBC4' : '#48182F',
                                color: isActive ? '#48182F' : 'white',
                                fontSize: '13px',
                                fontWeight: '600',
                                textAlign: 'center',
                                position: 'relative',
                                clipPath: isFirst
                                  ? 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%)'
                                  : isLast
                                  ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 20px 50%)'
                                  : 'polygon(0 0, calc(100% - 20px) 0, 100% 50%, calc(100% - 20px) 100%, 0 100%, 20px 50%)',
                                marginRight: isLast ? '0' : '-20px',
                                transform: isActive ? 'scale(1.05)' : 'scale(1)',
                                zIndex: isActive ? 1 : 0,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap',
                                cursor: 'pointer'
                              }}
                            >
                              {stage}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {phaseSelections.length === 0 ? (
                      <div className="unit-deal-empty-state" style={{ padding: '40px' }}>
                        <p>No selections made for Phase {activePhaseTab}</p>
                      </div>
                    ) : (
                      <>
                    {/* Document Generation Buttons Card */}
                    <div className="unit-deal-section">
                      <div style={{
                        padding: '20px 0',
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          className="btn btn-primary"
                          onClick={handleGenerateColorSelectionAmendment}
                          disabled={generatingPdf}
                          style={{
                            backgroundColor: '#48182F',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          {generatingPdf ? 'Generating...' : 'Generate Color Selection Amendment'}
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            toast.info('Generate Amendment - Coming soon');
                          }}
                          style={{
                            backgroundColor: '#48182F',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          Generate Amendment
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            toast.info('Generate Revised Amendment - Coming soon');
                          }}
                          style={{
                            backgroundColor: '#48182F',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          Generate Revised Amendment
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            toast.info('Generate Invoice - Coming soon');
                          }}
                          style={{
                            backgroundColor: '#48182F',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        >
                          Generate Invoice
                        </button>
                      </div>
                    </div>

                    {/* Color Collections for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'color').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Color Collection</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'color')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('color', activePhaseTab)) {
                                            toast.error(`Color collection can only be modified when Phase One status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses['One']}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('color', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('color', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('color', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('color', activePhaseTab) ? `Locked: Phase One status is ${phaseStatuses['One']}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'color')
                                    .reduce((sum, s) => sum + s.cost, 0)
                                    .toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'color')
                                    .reduce((sum, s) => sum + s.price, 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Packages for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'package').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Packages</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Location</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'package')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td>{selection.location || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('package', activePhaseTab)) {
                                            toast.error(`Packages can only be modified when Phase ${activePhaseTab} status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses[activePhaseTab]}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('package', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('package', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('package', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('package', activePhaseTab) ? `Locked: Phase ${activePhaseTab} status is ${phaseStatuses[activePhaseTab]}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'package')
                                    .reduce((sum, s) => sum + s.cost, 0)
                                    .toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'package')
                                    .reduce((sum, s) => sum + s.price, 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Options for this phase */}
                    {phaseSelections.filter(s => s.selectionType === 'option').length > 0 && (
                      <div className="unit-deal-section">
                        <div className="unit-deal-section-header">
                          <h3>Options</h3>
                        </div>
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Location</th>
                                <th>Category</th>
                                <th style={{ textAlign: 'right' }}>Cost</th>
                                <th style={{ textAlign: 'right' }}>Price</th>
                                <th style={{ textAlign: 'center' }}>Info</th>
                                <th style={{ textAlign: 'center' }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phaseSelections
                                .filter(s => s.selectionType === 'option')
                                .map((selection) => (
                                  <tr key={selection._id}>
                                    <td className="data-table-primary-cell">{selection.itemName}</td>
                                    <td>{selection.location || '-'}</td>
                                    <td>{selection.categoryName || '-'}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.cost.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>${selection.price.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center' }}>
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <button
                                          className="btn btn-sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setInfoModalData({
                                              title: selection.itemName,
                                              description: selection.description,
                                              constructionNotes: selection.constructionNotes
                                            });
                                            setShowInfoModal(true);
                                          }}
                                          title="View Details"
                                          style={{
                                            width: '32px',
                                            height: '32px',
                                            borderRadius: '50%',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#48182F',
                                            color: 'white',
                                            border: 'none',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            fontStyle: 'italic',
                                            cursor: 'pointer',
                                            transition: 'opacity 0.2s'
                                          }}
                                          onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                                          onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                        >
                                          i
                                        </button>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (!canModifySelections('option', activePhaseTab)) {
                                            toast.error(`Options can only be modified when Phase ${activePhaseTab} status is "NOT STARTED" or "OPEN". Current status: ${phaseStatuses[activePhaseTab]}`);
                                          } else {
                                            handleDeletePhaseSelection(selection._id, selection.selectionType);
                                          }
                                        }}
                                        disabled={!canModifySelections('option', activePhaseTab) || deletingSelection === selection._id}
                                        style={{
                                          backgroundColor: '#EF4444',
                                          color: 'white',
                                          opacity: (!canModifySelections('option', activePhaseTab) || deletingSelection === selection._id) ? 0.7 : 1,
                                          cursor: !canModifySelections('option', activePhaseTab) ? 'not-allowed' : 'pointer'
                                        }}
                                        title={!canModifySelections('option', activePhaseTab) ? `Locked: Phase ${activePhaseTab} status is ${phaseStatuses[activePhaseTab]}` : ''}
                                      >
                                        {deletingSelection === selection._id ? 'Deleting...' : 'Delete'}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              <tr style={{ fontWeight: '600', borderTop: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                                <td className="data-table-primary-cell">Total</td>
                                <td></td>
                                <td></td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'option')
                                    .reduce((sum, s) => sum + s.cost, 0)
                                    .toFixed(2)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  ${phaseSelections
                                    .filter(s => s.selectionType === 'option')
                                    .reduce((sum, s) => sum + s.price, 0)
                                    .toFixed(2)}
                                </td>
                                <td></td>
                                <td></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {activeTab === 'parking-selection' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Parking Selection</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'bicycle-parking-selection' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Bicycle Parking Selection</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'locker-selection' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Locker Selection</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'layout-customizations' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Layout Customizations</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'summary' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                <h3>Summary</h3>
                <p>Content coming soon</p>
              </div>
            )}
            {activeTab === 'documents' && (
              <div style={{ padding: '24px', gridColumn: '1 / -1' }}>
                {unitDeal?.dealId ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Décor Studio Amendments */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="decor_studio_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Décor Studio Amendments"
                      />
                    </div>

                    {/* Preferred Parking Amendments */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="preferred_parking_amendment"
                        acceptTypes=".pdf,.doc,.docx"
                        title="Preferred Parking Amendments"
                      />
                    </div>

                    {/* PDI */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="pdi"
                        acceptTypes=".pdf,.doc,.docx"
                        title="PDI"
                      />
                    </div>

                    {/* CCP */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="ccp"
                        acceptTypes=".pdf,.doc,.docx"
                        title="CCP"
                      />
                    </div>

                    {/* FAQ */}
                    <div className="unit-deal-document-card">
                      <DocumentGallery
                        workspaceId={workspaceId}
                        entityType="DEAL_DOCUMENTS"
                        entityId={unitDeal.dealId}
                        fileType="faq"
                        acceptTypes=".pdf,.doc,.docx"
                        title="FAQ"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="unit-deal-empty-state">
                    <p>No deal associated with this unit. Documents cannot be uploaded.</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={handleCancelChange}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>
              Confirmation
            </h3>
            <p style={{ marginBottom: '24px', color: '#6B7280', lineHeight: '1.5' }}>
              Changing color collection will reset the selection made on the Package and Personalization Option. Do you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={handleCancelChange}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfirmChange}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Modal */}
      {showInfoModal && infoModalData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setShowInfoModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '0',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Information
              </h3>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '0',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div style={{
              padding: '24px',
              overflowY: 'auto',
              flex: 1
            }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Description :
                </label>
                <div style={{
                  color: '#6B7280',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {stripHtmlTags(infoModalData.description) || '-'}
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  color: '#374151'
                }}>
                  Construction Notes :
                </label>
                <div style={{
                  color: '#6B7280',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {stripHtmlTags(infoModalData.constructionNotes) || '-'}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowInfoModal(false)}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Dialog */}
      {showStatusConfirmDialog && pendingStatusChange && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            maxWidth: '500px',
            width: '100%'
          }}>
            {/* Header */}
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827'
              }}>
                Confirm Status Change
              </h3>
            </div>

            {/* Body */}
            <div style={{
              padding: '24px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6'
            }}>
              Do you want to change the status to <strong>{pendingStatusChange.status}</strong>?
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <button
                onClick={handleCancelStatusChange}
                disabled={updatingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#E5E7EB',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingStatus ? 0.5 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmStatusChange}
                disabled={updatingStatus}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#48182F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: updatingStatus ? 0.5 : 1
                }}
              >
                {updatingStatus ? 'Updating...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitDealDetailsSlider;
